import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Card, 
  CardContent, 
  Grid, 
  TextField,
  Button,
  Paper,
  Divider,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import { 
  Calculate, 
  TrendingUp, 
  AttachMoney,
  Home,
  Build,
  AccountBalance,
  Timeline,
  Info
} from '@mui/icons-material';

const ROICalculator = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [inputs, setInputs] = useState({
    purchasePrice: '',
    downPayment: '',
    loanAmount: '',
    interestRate: '',
    loanTerm: '30',
    renovationCosts: '',
    monthlyRent: '',
    vacancy: '5',
    propertyTax: '',
    insurance: '',
    maintenance: '1',
    propertyManagement: '8',
    otherExpenses: '',
    investmentStrategy: 'rental'
  });

  const [results, setResults] = useState({
    totalInvestment: 0,
    monthlyMortgage: 0,
    monthlyExpenses: 0,
    netMonthlyIncome: 0,
    annualIncome: 0,
    cashOnCashReturn: 0,
    totalROI: 0,
    breakEvenPoint: 0,
    fiveYearProfit: 0
  });

  const [flipInputs, setFlipInputs] = useState({
    purchasePrice: '',
    renovationCosts: '',
    holdingCosts: '',
    sellingCosts: '',
    expectedSalePrice: '',
    timeframe: '6'
  });

  const [flipResults, setFlipResults] = useState({
    totalInvestment: 0,
    profit: 0,
    roi: 0,
    annualizedReturn: 0
  });

  const calculateRental = () => {
    const purchase = parseFloat(inputs.purchasePrice) || 0;
    const down = parseFloat(inputs.downPayment) || 0;
    const loan = parseFloat(inputs.loanAmount) || (purchase - down);
    const rate = parseFloat(inputs.interestRate) / 100 / 12 || 0;
    const term = parseInt(inputs.loanTerm) * 12 || 360;
    const renovation = parseFloat(inputs.renovationCosts) || 0;
    const rent = parseFloat(inputs.monthlyRent) || 0;
    const vacancy = parseFloat(inputs.vacancy) / 100 || 0.05;
    const tax = parseFloat(inputs.propertyTax) / 12 || 0;
    const insurance = parseFloat(inputs.insurance) / 12 || 0;
    const maintenance = (purchase * parseFloat(inputs.maintenance) / 100) / 12 || 0;
    const management = rent * parseFloat(inputs.propertyManagement) / 100 || 0;
    const other = parseFloat(inputs.otherExpenses) || 0;

    // Calculate monthly mortgage payment
    const monthlyMortgage = loan > 0 ? 
      loan * (rate * Math.pow(1 + rate, term)) / (Math.pow(1 + rate, term) - 1) : 0;

    // Calculate totals
    const totalInvestment = down + renovation;
    const effectiveRent = rent * (1 - vacancy);
    const monthlyExpenses = monthlyMortgage + tax + insurance + maintenance + management + other;
    const netMonthlyIncome = effectiveRent - monthlyExpenses;
    const annualIncome = netMonthlyIncome * 12;
    const cashOnCashReturn = totalInvestment > 0 ? (annualIncome / totalInvestment) * 100 : 0;
    const totalROI = totalInvestment > 0 ? ((annualIncome * 5 + (purchase * 1.03)) / totalInvestment - 1) * 100 : 0;
    const breakEvenPoint = netMonthlyIncome > 0 ? totalInvestment / netMonthlyIncome : 0;
    const fiveYearProfit = annualIncome * 5;

    setResults({
      totalInvestment,
      monthlyMortgage,
      monthlyExpenses,
      netMonthlyIncome,
      annualIncome,
      cashOnCashReturn,
      totalROI,
      breakEvenPoint,
      fiveYearProfit
    });
  };

  const calculateFlip = () => {
    const purchase = parseFloat(flipInputs.purchasePrice) || 0;
    const renovation = parseFloat(flipInputs.renovationCosts) || 0;
    const holding = parseFloat(flipInputs.holdingCosts) || 0;
    const selling = parseFloat(flipInputs.sellingCosts) || 0;
    const salePrice = parseFloat(flipInputs.expectedSalePrice) || 0;
    const months = parseFloat(flipInputs.timeframe) || 6;

    const totalInvestment = purchase + renovation + holding + selling;
    const profit = salePrice - totalInvestment;
    const roi = totalInvestment > 0 ? (profit / totalInvestment) * 100 : 0;
    const annualizedReturn = months > 0 ? (roi * 12) / months : 0;

    setFlipResults({
      totalInvestment,
      profit,
      roi,
      annualizedReturn
    });
  };

  useEffect(() => {
    calculateRental();
  }, [inputs]);

  useEffect(() => {
    calculateFlip();
  }, [flipInputs]);

  const handleInputChange = (field, value) => {
    setInputs(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFlipInputChange = (field, value) => {
    setFlipInputs(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(value);
  };

  const formatPercentage = (value) => {
    return `${value.toFixed(2)}%`;
  };

  const TabPanel = ({ children, value, index }) => (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8f9fa', py: 4 }}>
      <Container maxWidth="lg">
        {/* Header Section */}
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography variant="h2" component="h1" sx={{ 
            fontWeight: 700, 
            color: '#1B5E20',
            mb: 2 
          }}>
            ROI Calculator
          </Typography>
          <Typography variant="h6" sx={{ 
            color: '#666',
            maxWidth: '800px',
            mx: 'auto',
            lineHeight: 1.6
          }}>
            Calculate potential returns on your real estate investments. 
            Analyze rental properties and fix-and-flip opportunities.
          </Typography>
        </Box>

        {/* Calculator Tabs */}
        <Paper sx={{ mb: 4 }}>
          <Tabs 
            value={activeTab} 
            onChange={(e, newValue) => setActiveTab(newValue)}
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab label="Rental Property ROI" icon={<Home />} />
            <Tab label="Fix & Flip ROI" icon={<Build />} />
          </Tabs>

          {/* Rental Property Calculator */}
          <TabPanel value={activeTab} index={0}>
            <Grid container spacing={4}>
              {/* Input Section */}
              <Grid item xs={12} md={6}>
                <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, color: '#1B5E20' }}>
                  Property Details
                </Typography>
                
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Purchase Price"
                      type="number"
                      value={inputs.purchasePrice}
                      onChange={(e) => handleInputChange('purchasePrice', e.target.value)}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">$</InputAdornment>
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Down Payment"
                      type="number"
                      value={inputs.downPayment}
                      onChange={(e) => handleInputChange('downPayment', e.target.value)}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">$</InputAdornment>
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Interest Rate"
                      type="number"
                      value={inputs.interestRate}
                      onChange={(e) => handleInputChange('interestRate', e.target.value)}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">%</InputAdornment>
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel>Loan Term</InputLabel>
                      <Select
                        value={inputs.loanTerm}
                        label="Loan Term"
                        onChange={(e) => handleInputChange('loanTerm', e.target.value)}
                      >
                        <MenuItem value="15">15 years</MenuItem>
                        <MenuItem value="20">20 years</MenuItem>
                        <MenuItem value="30">30 years</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Renovation Costs"
                      type="number"
                      value={inputs.renovationCosts}
                      onChange={(e) => handleInputChange('renovationCosts', e.target.value)}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">$</InputAdornment>
                      }}
                    />
                  </Grid>
                </Grid>

                <Typography variant="h6" sx={{ fontWeight: 600, my: 3, color: '#1B5E20' }}>
                  Income & Expenses
                </Typography>

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Monthly Rent"
                      type="number"
                      value={inputs.monthlyRent}
                      onChange={(e) => handleInputChange('monthlyRent', e.target.value)}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">$</InputAdornment>
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Vacancy Rate"
                      type="number"
                      value={inputs.vacancy}
                      onChange={(e) => handleInputChange('vacancy', e.target.value)}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">%</InputAdornment>
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Annual Property Tax"
                      type="number"
                      value={inputs.propertyTax}
                      onChange={(e) => handleInputChange('propertyTax', e.target.value)}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">$</InputAdornment>
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Annual Insurance"
                      type="number"
                      value={inputs.insurance}
                      onChange={(e) => handleInputChange('insurance', e.target.value)}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">$</InputAdornment>
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Maintenance (% of value)"
                      type="number"
                      value={inputs.maintenance}
                      onChange={(e) => handleInputChange('maintenance', e.target.value)}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">%</InputAdornment>
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Property Management"
                      type="number"
                      value={inputs.propertyManagement}
                      onChange={(e) => handleInputChange('propertyManagement', e.target.value)}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">%</InputAdornment>
                      }}
                    />
                  </Grid>
                </Grid>
              </Grid>

              {/* Results Section */}
              <Grid item xs={12} md={6}>
                <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, color: '#1B5E20' }}>
                  Investment Analysis
                </Typography>

                <Card sx={{ mb: 3, bgcolor: '#e8f5e8' }}>
                  <CardContent>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="caption" sx={{ color: '#666' }}>
                          Total Investment
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 600, color: '#1B5E20' }}>
                          {formatCurrency(results.totalInvestment)}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" sx={{ color: '#666' }}>
                          Monthly Mortgage
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          {formatCurrency(results.monthlyMortgage)}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" sx={{ color: '#666' }}>
                          Monthly Expenses
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          {formatCurrency(results.monthlyExpenses)}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" sx={{ color: '#666' }}>
                          Net Monthly Income
                        </Typography>
                        <Typography variant="h6" sx={{ 
                          fontWeight: 600,
                          color: results.netMonthlyIncome >= 0 ? '#2e7d32' : '#d32f2f'
                        }}>
                          {formatCurrency(results.netMonthlyIncome)}
                        </Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>

                <Card sx={{ mb: 3 }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#1B5E20' }}>
                      Return Metrics
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="caption" sx={{ color: '#666' }}>
                          Annual Income
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          {formatCurrency(results.annualIncome)}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" sx={{ color: '#666' }}>
                          Cash-on-Cash Return
                        </Typography>
                        <Typography variant="h6" sx={{ 
                          fontWeight: 600,
                          color: results.cashOnCashReturn >= 8 ? '#2e7d32' : '#d32f2f'
                        }}>
                          {formatPercentage(results.cashOnCashReturn)}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" sx={{ color: '#666' }}>
                          5-Year ROI
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          {formatPercentage(results.totalROI)}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" sx={{ color: '#666' }}>
                          Break-even Point
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          {results.breakEvenPoint.toFixed(1)} months
                        </Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>

                {/* Performance Indicators */}
                <Alert 
                  severity={results.cashOnCashReturn >= 8 ? 'success' : results.cashOnCashReturn >= 4 ? 'warning' : 'error'}
                  sx={{ mb: 2 }}
                >
                  <Typography variant="body2">
                    {results.cashOnCashReturn >= 8 ? 
                      'Excellent investment! Strong cash-on-cash return.' :
                      results.cashOnCashReturn >= 4 ?
                      'Good investment with moderate returns.' :
                      'Consider reviewing your numbers. Low returns detected.'
                    }
                  </Typography>
                </Alert>
              </Grid>
            </Grid>
          </TabPanel>

          {/* Fix & Flip Calculator */}
          <TabPanel value={activeTab} index={1}>
            <Grid container spacing={4}>
              {/* Flip Input Section */}
              <Grid item xs={12} md={6}>
                <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, color: '#1B5E20' }}>
                  Fix & Flip Details
                </Typography>
                
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Purchase Price"
                      type="number"
                      value={flipInputs.purchasePrice}
                      onChange={(e) => handleFlipInputChange('purchasePrice', e.target.value)}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">$</InputAdornment>
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Renovation Costs"
                      type="number"
                      value={flipInputs.renovationCosts}
                      onChange={(e) => handleFlipInputChange('renovationCosts', e.target.value)}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">$</InputAdornment>
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Holding Costs (utilities, taxes, insurance)"
                      type="number"
                      value={flipInputs.holdingCosts}
                      onChange={(e) => handleFlipInputChange('holdingCosts', e.target.value)}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">$</InputAdornment>
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Selling Costs (realtor fees, closing costs)"
                      type="number"
                      value={flipInputs.sellingCosts}
                      onChange={(e) => handleFlipInputChange('sellingCosts', e.target.value)}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">$</InputAdornment>
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Expected Sale Price"
                      type="number"
                      value={flipInputs.expectedSalePrice}
                      onChange={(e) => handleFlipInputChange('expectedSalePrice', e.target.value)}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">$</InputAdornment>
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Project Timeframe"
                      type="number"
                      value={flipInputs.timeframe}
                      onChange={(e) => handleFlipInputChange('timeframe', e.target.value)}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">months</InputAdornment>
                      }}
                    />
                  </Grid>
                </Grid>
              </Grid>

              {/* Flip Results Section */}
              <Grid item xs={12} md={6}>
                <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, color: '#1B5E20' }}>
                  Flip Analysis
                </Typography>

                <Card sx={{ mb: 3, bgcolor: '#e8f5e8' }}>
                  <CardContent>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Typography variant="caption" sx={{ color: '#666' }}>
                          Total Investment
                        </Typography>
                        <Typography variant="h5" sx={{ fontWeight: 600, color: '#1B5E20' }}>
                          {formatCurrency(flipResults.totalInvestment)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="caption" sx={{ color: '#666' }}>
                          Expected Profit
                        </Typography>
                        <Typography variant="h5" sx={{ 
                          fontWeight: 600,
                          color: flipResults.profit >= 0 ? '#2e7d32' : '#d32f2f'
                        }}>
                          {formatCurrency(flipResults.profit)}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" sx={{ color: '#666' }}>
                          ROI
                        </Typography>
                        <Typography variant="h6" sx={{ 
                          fontWeight: 600,
                          color: flipResults.roi >= 20 ? '#2e7d32' : '#d32f2f'
                        }}>
                          {formatPercentage(flipResults.roi)}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" sx={{ color: '#666' }}>
                          Annualized Return
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          {formatPercentage(flipResults.annualizedReturn)}
                        </Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>

                <Alert 
                  severity={flipResults.roi >= 20 ? 'success' : flipResults.roi >= 10 ? 'warning' : 'error'}
                  sx={{ mb: 2 }}
                >
                  <Typography variant="body2">
                    {flipResults.roi >= 20 ? 
                      'Excellent flip opportunity! Strong profit margins.' :
                      flipResults.roi >= 10 ?
                      'Good flip potential with moderate returns.' :
                      'Consider adjusting your numbers. Low profit margins detected.'
                    }
                  </Typography>
                </Alert>

                {/* Flip Cost Breakdown Table */}
                <TableContainer component={Paper}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Cost Category</TableCell>
                        <TableCell align="right">Amount</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell>Purchase Price</TableCell>
                        <TableCell align="right">
                          {formatCurrency(parseFloat(flipInputs.purchasePrice) || 0)}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Renovation Costs</TableCell>
                        <TableCell align="right">
                          {formatCurrency(parseFloat(flipInputs.renovationCosts) || 0)}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Holding Costs</TableCell>
                        <TableCell align="right">
                          {formatCurrency(parseFloat(flipInputs.holdingCosts) || 0)}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Selling Costs</TableCell>
                        <TableCell align="right">
                          {formatCurrency(parseFloat(flipInputs.sellingCosts) || 0)}
                        </TableCell>
                      </TableRow>
                      <TableRow sx={{ fontWeight: 'bold' }}>
                        <TableCell><strong>Total Investment</strong></TableCell>
                        <TableCell align="right">
                          <strong>{formatCurrency(flipResults.totalInvestment)}</strong>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
            </Grid>
          </TabPanel>
        </Paper>

        {/* ROI Tips */}
        <Paper sx={{ p: 4, bgcolor: '#1B5E20', color: 'white' }}>
          <Typography variant="h4" sx={{ fontWeight: 600, mb: 3, textAlign: 'center' }}>
            ROI Calculation Tips
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'center' }}>
                <Calculate sx={{ fontSize: 48, mb: 2, opacity: 0.8 }} />
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  Be Conservative
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Always use conservative estimates for income and liberal estimates for expenses
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'center' }}>
                <Timeline sx={{ fontSize: 48, mb: 2, opacity: 0.8 }} />
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  Account for All Costs
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Include vacancy, maintenance, management, and unexpected expenses in your calculations
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'center' }}>
                <TrendingUp sx={{ fontSize: 48, mb: 2, opacity: 0.8 }} />
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  Market Conditions
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Consider current market trends and economic factors that may affect your returns
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </Container>
    </Box>
  );
};

export default ROICalculator;














